import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useProductStore } from "./store/productStore";
import { useCartStore } from "./store/cartStore";
import ProductCard from "./ProductCard";
import HeroCarousel from "./components/HeroCarousel";
import { ShootingStars, StarsBackground } from "./components/ShootingStars";
import { useTranslation } from "react-i18next";
import Testimonials from "./components/Testimonials";
import CateringSection from "./components/CateringSection";
import HoursSection from "./components/HoursSection";

export default function Home() {
  const products = useProductStore((state) => state.products);
  const fetchProducts = useProductStore((state) => state.fetchProducts);
  const loading = useProductStore((state) => state.loading);
  const addItem = useCartStore((state) => state.addItem);
  const { t } = useTranslation();

  const popularProducts = React.useMemo(() => {
    const shuffled = [...products].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 4);
  }, [products]);

  useEffect(() => {
    fetchProducts({ limit: 12 });
  }, [fetchProducts]);

  return (
    <div className="space-y-16">
      {/* PREMIUM HERO SECTION - Full Height & Maximum Impact */}
      <section className="relative overflow-hidden rounded-2xl shadow-premium -mt-4">
        <div className="relative">
          {/* Hero Carousel - Taller for More Impact */}
          <div className="relative h-[600px] md:h-[700px]">
            <HeroCarousel products={products} />
            
            {/* Shooting Stars Animation */}
            <ShootingStars 
              minSpeed={15}
              maxSpeed={25}
              starColor="#FDE047"
              trailColor="#FBBF24"
              className="z-[5]"
            />
            
            {/* Stars Background */}
            <StarsBackground 
              starDensity={0.0002}
              className="z-[4]"
            />
            
            {/* Animated Geometric Pattern Overlay */}
            <div className="absolute inset-0 opacity-10 pointer-events-none">
              <div className="absolute top-0 left-0 w-full h-full animate-pattern-float"
                   style={{ 
                     backgroundImage: "radial-gradient(circle at 20% 50%, var(--raya-accent-light) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(6, 78, 59, 0.3) 0%, transparent 50%)",
                   }}>
              </div>
            </div>
            
            {/* Premium Gradient Overlay for Better Text Contrast */}
            <div className="absolute inset-0 bg-gradient-to-t from-digital-noir/80 via-digital-noir/40 to-digital-noir/20"></div>
            
            {/* Hero Content - Centered and Impactful */}
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center z-10 px-4" style={{ paddingTop: '40px' }}>
              <div className="w-full max-w-4xl mx-auto animate-fade-in-up">
                {/* Badge Overlay */}
                <div className="inline-block mb-6 md:mb-8">
                  <span className="bg-gradient-to-r from-raya-accent to-raya-accent-hover text-black px-4 py-1.5 md:px-6 md:py-2.5 rounded-full text-xs md:text-sm font-bold tracking-wider animate-pulse-subtle shadow-lg">
                    ✨ {t('home.badge')} ✨
                  </span>
                </div>
                
                {/* Main Heading - Maximum Impact */}
                <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-serif font-bold mb-6 md:mb-8 tracking-tight text-white drop-shadow-2xl leading-tight">
                  {t('home.welcome')}
                </h1>
                
                {/* Description */}
                <p className="text-base sm:text-lg md:text-2xl font-medium mb-10 md:mb-14 max-w-2xl mx-auto text-white/90 drop-shadow-lg leading-relaxed">
                  {t('home.description')}
                </p>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-4 md:gap-8 justify-center items-center w-full max-w-xl mx-auto">
                  <Link to="/products" className="w-full sm:w-auto">
                    <button className="w-full sm:w-auto bg-raya-accent hover:bg-raya-accent-hover text-black font-extrabold px-8 py-4 md:px-12 md:py-5 rounded-full transition-all text-base md:text-xl shadow-[0_0_20px_rgba(251,191,36,0.4)] hover:shadow-[0_0_30px_rgba(251,191,36,0.6)] transform hover:scale-105 active:scale-95">
                      {t('home.cta')}
                    </button>
                  </Link>
                  <Link to="/about" className="w-full sm:w-auto">
                    <button className="w-full sm:w-auto bg-white/10 hover:bg-white/20 backdrop-blur-md text-white font-bold px-8 py-4 md:px-12 md:py-5 rounded-full transition-all text-base md:text-xl border-2 border-white/30 hover:border-white/60 transform hover:scale-105 active:scale-95">
                      {t('home.learnMore')}
                    </button>
                  </Link>
                </div>
              </div>
            </div>

            {/* Decorative Elements - Floating Shapes */}
            <div className="absolute top-20 left-10 w-20 h-20 border-4 border-raya-accent/20 rounded-full animate-float-slow hidden md:block"></div>
            <div className="absolute bottom-32 right-16 w-16 h-16 border-4 border-emerald-400/20 rounded-lg rotate-45 animate-float-medium hidden md:block"></div>
            <div className="absolute top-1/2 right-10 w-12 h-12 bg-gradient-to-br from-yellow-400/10 to-emerald-400/10 rounded-full animate-float-fast hidden md:block"></div>
          </div>
        </div>
      </section>


      {/* LUXURY FEATURES - Enhanced Design */}
      <section>
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-3">
            <span className="bg-gradient-premium bg-clip-text text-transparent">
              {t('home.whyChoose')}
            </span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-raya-accent to-transparent mx-auto"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="feature-card animate-scale-in text-center group" style={{ animationDelay: '0.1s' }}>
            <div className="feature-icon mx-auto transform group-hover:scale-110 transition-transform">
              <span className="text-4xl">🛡️</span>
            </div>
            <h3 className="text-2xl font-bold mb-3 bg-gradient-to-r from-raya-accent to-raya-accent-hover bg-clip-text text-transparent">
              {t('home.securePayments')}
            </h3>
            <p className="text-raya-text-secondary leading-relaxed">
              {t('home.securePaymentsDesc')}
            </p>
            <div className="mt-6 h-1 w-16 bg-gradient-to-r from-raya-accent to-raya-accent-hover mx-auto transform scale-x-0 group-hover:scale-x-100 transition-transform"></div>
          </div>

          <div className="feature-card animate-scale-in text-center group" style={{ animationDelay: '0.2s' }}>
            <div className="feature-icon mx-auto transform group-hover:scale-110 transition-transform">
              <span className="text-4xl">🚚</span>
            </div>
            <h3 className="text-2xl font-bold mb-3 bg-gradient-to-r from-raya-accent to-raya-accent-hover bg-clip-text text-transparent">
              {t('home.fastDelivery')}
            </h3>
            <p className="text-raya-text-secondary leading-relaxed">
              {t('home.fastDeliveryDesc')}
            </p>
            <div className="mt-6 h-1 w-16 bg-gradient-to-r from-raya-accent to-raya-accent-hover mx-auto transform scale-x-0 group-hover:scale-x-100 transition-transform"></div>
          </div>

          <div className="feature-card animate-scale-in text-center group" style={{ animationDelay: '0.3s' }}>
            <div className="feature-icon mx-auto transform group-hover:scale-110 transition-transform">
              <span className="text-4xl">💬</span>
            </div>
            <h3 className="text-2xl font-bold mb-3 bg-gradient-to-r from-raya-accent to-raya-accent-hover bg-clip-text text-transparent">
              {t('home.support')}
            </h3>
            <p className="text-raya-text-secondary leading-relaxed">
              {t('home.supportDesc')}
            </p>
            <div className="mt-6 h-1 w-16 bg-gradient-to-r from-raya-accent to-raya-accent-hover mx-auto transform scale-x-0 group-hover:scale-x-100 transition-transform"></div>
          </div>
        </div>
      </section>

      {/* PREMIUM PRODUCTS SHOWCASE */}
      <section>
        <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold mb-2">
              <span className="bg-gradient-premium bg-clip-text text-transparent">
                {t('home.popularProducts')}
              </span>
            </h2>
            <p className="text-raya-text-secondary">{t('home.handpickedFavorites')}</p>
          </div>
          
          <Link to="/products" className="group flex items-center gap-2 text-raya-accent font-medium hover:text-raya-accent-hover transition px-4 py-2 rounded-full hover:bg-raya-accent-light">
            {t('home.viewAllProducts')} →
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-raya-card rounded-xl shadow-md overflow-hidden animate-pulse-subtle">
                <div className="skeleton h-64 md:h-96" />
                <div className="p-4 space-y-3">
                  <div className="skeleton h-6 w-3/4 rounded" />
                  <div className="skeleton h-4 w-1/2 rounded" />
                  <div className="skeleton h-10 w-full rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : popularProducts.length === 0 ? (
          <div className="text-center py-16 bg-raya-section rounded-2xl shadow-md">
            <div className="text-6xl mb-4">📦</div>
            <p className="text-raya-text-secondary text-lg">{t('home.noProducts')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {popularProducts.map((product, index) => (
              <div 
                key={product.id}
                className="animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <ProductCard 
                  product={product} 
                  onAddToCart={addItem} 
                />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* CATERING SECTION */}
      <CateringSection />

      {/* TESTIMONIALS SECTION */}
      <Testimonials />

      {/* HOURS OF OPERATION SECTION */}
      <HoursSection />
    </div>
  );
}

// force-git-sync
